import * as Dialog from '@radix-ui/react-dialog';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import React, { type ReactNode } from 'react';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { tv } from '@/utils/tv';

const content = tv({
  base: 'bg-background fixed inset-0 z-50 flex flex-col outline-none md:inset-auto md:top-1/2 md:left-1/2 md:h-[min(788px,calc(100svh-80px))] md:w-[min(1300px,calc(100vw-80px))] md:-translate-x-1/2 md:-translate-y-1/2 md:overflow-hidden md:rounded-xl md:shadow-modal',
  variants: {
    hideBorder: {
      false: 'md:border md:border-border',
      true: '',
    },
  },
  defaultVariants: {
    hideBorder: false,
  },
});

interface Props {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  className?: string;
  hideBorder?: boolean;
  autoHideScrollbar?: boolean;
  onClose: () => void;
  onBack?: () => void;
}

export const TileModal: React.FC<Props> = ({
  isOpen,
  title,
  children,
  className,
  hideBorder = false,
  autoHideScrollbar = false,
  onClose,
  onBack,
}) => {
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();
  const isMobile = useIsMobile();
  const showScrollArea = autoHideScrollbar && !isMobile;
  const leftAction = onBack ?? onClose;

  useCloseOnBack(isOpen, leftAction);

  const scrollContent = (
    <>
      <h2
        style={{ opacity: 1 - scrollProgress }}
        className="text-title-30 text-foreground px-4 pt-2 pb-4 transition-opacity duration-75 md:hidden"
      >
        {title}
      </h2>
      {children}
    </>
  );

  const handleOpenChange = (isNextOpen: boolean) => {
    if (!isNextOpen) {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className={content({ hideBorder, className })}
        >
          <div className="relative flex w-full items-center justify-between gap-3 px-4 py-4 md:hidden">
            <button
              type="button"
              onClick={leftAction}
              className="text-foreground flex size-6 shrink-0 cursor-pointer items-center justify-center"
              aria-label={title}
            >
              <ArrowLeft className="size-6" />
            </button>
            <Dialog.Title
              style={{ opacity: scrollProgress }}
              className="text-callout-16 text-foreground pointer-events-none absolute left-1/2 -translate-x-1/2 transition-opacity duration-75"
            >
              {title}
            </Dialog.Title>
            <span className="size-6 shrink-0" />
          </div>

          <div className="hidden items-center justify-between gap-3 px-6 py-4 md:flex">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="text-foreground flex size-6 shrink-0 cursor-pointer items-center justify-center"
                  aria-label={title}
                >
                  <ArrowLeft className="size-6" />
                </button>
              )}
              <Dialog.Title className="text-headline-20 truncate text-foreground">
                {title}
              </Dialog.Title>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground flex size-4 shrink-0 cursor-pointer items-center justify-center transition-colors"
              aria-label={title}
            >
              <Close className="size-4" />
            </button>
          </div>

          {showScrollArea && (
            <ScrollArea.Root
              type="scroll"
              className="flex min-h-0 flex-1 flex-col"
            >
              <ScrollArea.Viewport
                onScroll={handleScroll}
                className="size-full overscroll-contain [&>div]:!flex [&>div]:!min-h-full [&>div]:!flex-col"
              >
                {scrollContent}
              </ScrollArea.Viewport>
              <ScrollArea.Scrollbar
                orientation="vertical"
                forceMount
                className="z-10 flex w-[8px] touch-none pr-[0.5px] opacity-70 transition-opacity duration-300 ease-out select-none data-[state=hidden]:opacity-0"
              >
                <ScrollArea.Thumb className="relative flex-1 rounded-full bg-[var(--scrollbar-thumb)]" />
              </ScrollArea.Scrollbar>
            </ScrollArea.Root>
          )}

          {!showScrollArea && (
            <div
              onScroll={handleScroll}
              className="relative flex flex-1 flex-col overflow-y-auto overscroll-contain"
            >
              {scrollContent}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
